import { IsPhaseAtLeast, IsWindowOpen, StageOf, type IOEPResource, type IOEPSeasonTimeline, type OEPDraftOrSubmitted, type OEPPhase } from 'agu_oep_core';
import { CRITERION_SHORT, DayMonth, DaysUntil } from '../shared/format';
import { PresenterMeta } from '../shared/presenter';

/** One rated criterion of a nomination or a released review (Sequence 1-5 = logic.js RUBRIC order). */
export interface OEPCommScoreInput { Sequence: number; Level: number | null; LevelLabel: string; Text: string }

/** A Submitted nomination in my section (committee RLS, T5: Submitted only, from Selecting on). */
export interface OEPCommNominationInput {
    NominationID: string;
    PresentationID: string;
    NominatorName: string;
    /** The nominator's Participant row (readable by the committee for Submitted nominations, T5). */
    NominatorInstitution: string | null;
    Scores: OEPCommScoreInput[];
    AdditionalReasoning: string | null;
}

/** A released (Completed) review of a nominated presentation (committee RLS, T5). */
export interface OEPCommReviewInput {
    ReviewID: string;
    PresentationID: string;
    Scores: OEPCommScoreInput[];
    Strengths: string;
    Suggestions: string;
}

/** A live presentation in my section. */
export interface OEPCommPresentationInput {
    PresentationID: string;
    Title: string;
    PresenterName: string;
    Institution: string | null;
    CareerStage: string | null;
    Format: string | null;
    Code: string | null;
    /** The newest Uploaded OEP Final (committees see finals only — T5). */
    FinalFileID: string | null;
}

/** A presentation with at least one Submitted nomination: one card and one row of the score export. */
export interface OEPCommNominee extends OEPCommPresentationInput {
    Nominations: OEPCommNominationInput[];
    Reviews: OEPCommReviewInput[];
    /** Its Committee Selection Items row on my section's slate, or null (not picked). */
    ItemID: string | null;
}

export interface OEPCommSelectionInput { ID: string; Status: OEPDraftOrSubmitted; SubmittedAt: Date | null }

/**
 * Groups the section's nominations by presentation: one nominee per presentation with ≥1 Submitted
 * nomination (a presentation nominated twice is one card with both nominators), in the order the
 * presentations come (code, title). Reviews and slate items are attached by presentation.
 */
export function GroupNominees(
    presentations: readonly OEPCommPresentationInput[],
    nominations: readonly OEPCommNominationInput[],
    reviews: readonly OEPCommReviewInput[],
    items: readonly { ID: string; PresentationID: string }[],
): OEPCommNominee[] {
    const up = (s: string): string => s.toUpperCase();
    return presentations.flatMap((p) => {
        const pid = up(p.PresentationID);
        const noms = nominations.filter((n) => up(n.PresentationID) === pid);
        if (noms.length === 0) return [];
        return [{
            ...p,
            Nominations: noms,
            Reviews: reviews.filter((r) => up(r.PresentationID) === pid),
            ItemID: items.find((i) => up(i.PresentationID) === pid)?.ID ?? null,
        }];
    });
}

export interface CommitteeInputs {
    Phase: OEPPhase;
    Timeline: IOEPSeasonTimeline;
    SeasonCode: string;
    /** Season.WinnersPerSection (the prototype's winCap). */
    Cap: number;
    SectionName: string;
    Now: Date;
    /** Season.Resources.committee (the locked view's links, markup.html:771-773). */
    Resources: IOEPResource[];
    /** Submitted nominations in the section (the "Nominations received" stat). */
    NominationCount: number;
    /** Live presentations in the section. */
    PresentationCount: number;
    Nominees: OEPCommNominee[];
    /**
     * Items on my section's slate — all of them, as the server counts slots (DecideSelectionItemAdd
     * UsedSlots), including one whose presentation no longer has a card (e.g. its nomination was withdrawn).
     */
    SlotsUsed: number;
    /** My section's slate for this season; null before the first pick. */
    Selection: OEPCommSelectionInput | null;
    /** Card IDs (presentation IDs, any case) whose details are expanded. */
    Expanded: ReadonlySet<string>;
    /** Scores were downloaded in this session (logic.js:721 exportLabel). */
    Exported: boolean;
}

/** The pick button (logic.js:376-384): Tone drives the style, Enabled mirrors DecideSelectionItemAdd / Remove. */
export interface OEPCommPick { Label: string; Enabled: boolean; Tone: 'on' | 'free' | 'full' | 'winner' | 'not' }

export interface OEPCommScoreVM { Label: string; Value: string; Justification: string | null }
export interface OEPCommNominationVM { Source: string; Scores: OEPCommScoreVM[]; Additional: string | null }
export interface OEPCommReviewVM { Heading: string; Scores: OEPCommScoreVM[]; Strengths: string; Suggestions: string | null }

export interface OEPCommNomineeVM {
    PresentationID: string;
    ItemID: string | null;
    DetailsID: string;
    /** "Nominated by {Nominator} · {Institution}", one line per nomination (logic.js:363). */
    Sources: string[];
    Title: string;
    Line: string;
    Picked: boolean;
    Pick: OEPCommPick;
    Expanded: boolean;
    ExpandLabel: string;
    Nominations: OEPCommNominationVM[];
    FinalFileID: string | null;
    Reviews: OEPCommReviewVM[];
}

export interface OEPCommStatVM { Value: string; Label: string }

export interface CommitteeOpenVM {
    Stats: OEPCommStatVM[];
    ScopeNote: string;
    ExportLabel: string;
    Exported: boolean;
    Nominees: OEPCommNomineeVM[];
    SelectedLabel: string;
    SubmitNote: string;
    SubmitLabel: string;
    CanSubmit: boolean;
    Submitted: boolean;
    PickedCount: number;
}

export interface CommitteeVM {
    Title: string;
    Intro: string;
    Locked: { Title: string; Body: string; Links: IOEPResource[] } | null;
    Open: CommitteeOpenVM | null;
}

/** Winner picks are editable only while the Selection window is open and the slate is a Draft (DecideSelectionItemAdd/Remove, decisions.ts:176-189). */
export function CanEditSlate(phase: OEPPhase, selection: Pick<OEPCommSelectionInput, 'Status'> | null): boolean {
    return IsWindowOpen('Selection', phase) && selection?.Status !== 'Submitted';
}

/** Submit mirrors DecideSelectionChange (decisions.ts:168-174): window open, not yet Submitted, at least one item. */
export function CanSubmitSlate(phase: OEPPhase, selection: Pick<OEPCommSelectionInput, 'Status'> | null, picked: number): boolean {
    return CanEditSlate(phase, selection) && selection !== null && picked > 0;
}

/** logic.js:377-378 pickLabel; a closed window (Complete) reads like a submitted slate. */
export function PickState(i: { Picked: boolean; PickedCount: number; Cap: number; Editable: boolean }): OEPCommPick {
    if (!i.Editable) return i.Picked ? { Label: 'Winner', Enabled: false, Tone: 'winner' } : { Label: 'Not selected', Enabled: false, Tone: 'not' };
    if (i.Picked) return { Label: 'Selected as winner ✓', Enabled: true, Tone: 'on' };
    return i.PickedCount >= i.Cap ? { Label: `${i.Cap} already selected`, Enabled: false, Tone: 'full' } : { Label: 'Select as winner', Enabled: true, Tone: 'free' };
}

/** logic.js:724 selectedLabel. */
export function SelectedLabel(picked: number, cap: number, submitted: boolean): string {
    return submitted ? 'Selections submitted to AGU' : `${picked} of ${cap} winners selected`;
}

/**
 * logic.js:725-729 submitNote ("26 January" → the slate's SubmittedAt). With the window closed and no
 * submitted slate (Complete), the server's refusal copy (decisions.ts:170).
 */
export function SubmitNote(i: { Phase: OEPPhase; Picked: number; Cap: number; Selection: OEPCommSelectionInput | null }): string {
    if (i.Selection?.Status === 'Submitted') {
        const on = i.Selection.SubmittedAt ? ` ${DayMonth(i.Selection.SubmittedAt)}` : '';
        return `Submitted${on}. Contact the OEP team if your committee needs to change its slate.`;
    }
    if (!IsWindowOpen('Selection', i.Phase)) return 'Winner selection is closed.';
    return i.Picked >= i.Cap
        ? `${i.Cap} is the maximum for this section. Deselect one if your committee wants to swap.`
        : 'Nothing is final until you submit. AGU staff see your section as complete once you do.';
}

/**
 * logic.js:708-713 commLockTitle / commLockBody, by stage. "7 January" → NominationsClose, "28 January"
 * → SelectionDue, "AGU26 in December" → "{SeasonCode}, {Meeting.Label}" (the task 27 convention), winCap →
 * WinnersPerSection. The `dec` body drops "17 nominations have arrived so far" (committees cannot read
 * nominations before Selecting, T5).
 */
export function LockedCopy(phase: OEPPhase, seasonCode: string, cap: number, t: IOEPSeasonTimeline): { Title: string; Body: string } {
    const d = t.Dates;
    const stage = StageOf(phase);
    const title = stage === 'dec' ? 'Nominations are still coming in' : `Your committee view opens ${d.NominationsClose.Label}`;
    if (stage === 'dec') {
        return { Title: title, Body: `Nominators submit through ${d.NominationsClose.Label}, and your view opens the moment they close. There is nothing to weigh yet. We will email you when selection opens.` };
    }
    if (stage === 'nov') {
        return { Title: title, Body: `Nominating happens at ${seasonCode}, ${d.Meeting.Label}, and closes ${d.NominationsClose.Label}; selection opens then and runs to ${d.SelectionDue.Label}. Now is the time to read the handbook and the award criteria. That is all this role needs from you until January.` };
    }
    return { Title: title, Body: `Presenters have not presented yet. Nominating opens at ${seasonCode}, ${d.Meeting.Label}, closes ${d.NominationsClose.Label}, and your committee then selects up to ${cap} winners by ${d.SelectionDue.Label}.` };
}

function scoreVMs(scores: readonly OEPCommScoreInput[], withJustification: boolean): OEPCommScoreVM[] {
    return [...scores]
        .filter((s) => s.Sequence >= 1 && s.Sequence <= CRITERION_SHORT.length)
        .sort((a, b) => a.Sequence - b.Sequence)
        .map((s) => ({
            Label: CRITERION_SHORT[s.Sequence - 1],
            Value: s.Level !== null ? `${s.LevelLabel} (${s.Level})` : 'Not scored',
            // The Outstanding justification (nomination TextValue on a 5, task 27).
            Justification: withJustification && s.Text.trim().length > 0 ? s.Text : null,
        }));
}

/** logic.js:363 source: "Nominated by {Nominator} · {Institution}" (institution left out when unknown). */
export function NominationSource(n: Pick<OEPCommNominationInput, 'NominatorName' | 'NominatorInstitution'>): string {
    return n.NominatorInstitution ? `Nominated by ${n.NominatorName} · ${n.NominatorInstitution}` : `Nominated by ${n.NominatorName}`;
}

function nomineeVM(n: OEPCommNominee, i: CommitteeInputs, picked: number, editable: boolean): OEPCommNomineeVM {
    const expanded = [...i.Expanded].some((x) => x.toUpperCase() === n.PresentationID.toUpperCase());
    const on = n.ItemID !== null;
    return {
        PresentationID: n.PresentationID,
        ItemID: n.ItemID,
        DetailsID: `oep-comm-details-${n.PresentationID}`,
        Sources: n.Nominations.map(NominationSource),
        Title: n.Title,
        // logic.js:364 line "{who} · {affil} · {stage} · Poster · Session: {code}" (Poster → the presentation's Format)
        Line: PresenterMeta(n),
        Picked: on,
        Pick: PickState({ Picked: on, PickedCount: picked, Cap: i.Cap, Editable: editable }),
        Expanded: expanded,
        ExpandLabel: expanded ? 'Hide details' : 'Nomination & feedback', // logic.js:369
        Nominations: n.Nominations.map((x) => ({
            Source: NominationSource(x),
            Scores: scoreVMs(x.Scores, true),
            Additional: x.AdditionalReasoning && x.AdditionalReasoning.trim().length > 0 ? x.AdditionalReasoning : null,
        })),
        FinalFileID: n.FinalFileID,
        Reviews: n.Reviews.map((r, idx) => ({
            Heading: `Reviewer ${idx + 1}`,
            Scores: scoreVMs(r.Scores, false),
            Strengths: r.Strengths,
            Suggestions: r.Suggestions.trim().length > 0 ? r.Suggestions : null,
        })),
    };
}

/** markup.html:762-841 / logic.js:356-385, 703-734 as one pure function. Every string cites its prototype line. */
export function BuildCommitteeVM(i: CommitteeInputs): CommitteeVM {
    const d = i.Timeline.Dates;
    const open = IsPhaseAtLeast(i.Phase, 'Selecting');
    const base = {
        Title: `Select ${i.SectionName} winners`, // markup.html:763
        // logic.js:705-707 commIntro ("7 January" → NominationsClose, "28 January" → SelectionDue, winCap → WinnersPerSection)
        Intro: open
            ? `Session nominators submitted nominations for presenters they felt gave outstanding presentations in their sessions. Nominations closed ${d.NominationsClose.Label}; your committee reads them all and selects winners for the section by ${d.SelectionDue.Label}. You can review the nomination forms, feedback on their draft presentations that presenters received from reviewers in November, and final presentations as uploaded by the presenter below.`
            : `Session nominators submit nominations for presenters they felt gave outstanding presentations in their sessions. Your committee reads them all and selects up to ${i.Cap} winners for the section.`,
    };
    if (!open) {
        return { ...base, Locked: { ...LockedCopy(i.Phase, i.SeasonCode, i.Cap, i.Timeline), Links: [...i.Resources] }, Open: null };
    }
    const picked = i.SlotsUsed;
    const submitted = i.Selection?.Status === 'Submitted';
    const editable = CanEditSlate(i.Phase, i.Selection);
    const days = DaysUntil(d.SelectionDue.On, i.Now);
    const count = i.Nominees.length;
    return {
        ...base,
        Locked: null,
        Open: {
            // logic.js:714-719 commStats
            Stats: [
                { Value: String(i.NominationCount), Label: 'Nominations received' },
                { Value: String(i.PresentationCount), Label: 'OEP presenters in section' },
                { Value: String(i.Cap), Label: 'Winners allowed' },
                { Value: `${days} ${days === 1 ? 'day' : 'days'}`, Label: `Until ${d.SelectionDue.Label}` },
            ],
            // logic.js:720 commScopeNote
            ScopeNote: `Winners are selected from the ${count} nominated ${count === 1 ? 'presentation' : 'presentations'}. The other presenters in the section are counted above but are not eligible for an award.`,
            ExportLabel: i.Exported ? 'Scores downloaded ✓' : 'Download all scores (.xlsx)', // logic.js:721
            Exported: i.Exported,
            Nominees: i.Nominees.map((n) => nomineeVM(n, i, picked, editable)),
            SelectedLabel: SelectedLabel(picked, i.Cap, submitted),
            SubmitNote: SubmitNote({ Phase: i.Phase, Picked: picked, Cap: i.Cap, Selection: i.Selection }),
            SubmitLabel: submitted ? 'Submitted ✓' : 'Submit selections', // logic.js:730
            CanSubmit: CanSubmitSlate(i.Phase, i.Selection, picked),
            Submitted: submitted,
            PickedCount: picked,
        },
    };
}
